/*
  to allow anon submissions: https://support.zendesk.com/hc/en-us/articles/4408881989018#topic_5m2_5qm_mj

  TLDR:
  - In Admin Center, click  People in the sidebar, then select Configuration > End users.
    - (https://{{ specificToClient }}.zendesk.com/admin/people/configuration/settings)
  - In the Anybody can submit tickets section, select Enabled.
  - If you use the Zendesk API to let users submit tickets, make sure Require authentication for request and uploads APIs is not selected.

  Form ID can be found in the URL when choosing form ID:
  https://{{ specificToClient }}.zendesk.com/admin/objects-rules/tickets/ticket-forms

  Field ID can be found in the admin in field list:
  https://{{ specificToClient }}.zendesk.com/admin/objects-rules/tickets/ticket-fields

  Tickets from forms can be found here:
  https://{{ specificToClient }}.zendesk.com/agent/dashboard

  TODOS:
  - currently files are mandatory, need to make them optional
  - should files only upload on submit? currently they upload on change
  - add radio input functionality
*/

class ZendeskForm extends window.BAO.CustomElement() {
  static get requiredElements () {
    return [
      'form',
      'email',
      'submit',
      'success',
      'error',
    ]
  }
  connectedCallback () {
    super.connectedCallback()

    this.formTag = this.getAttribute('data-form-tag')
    this.formID = Number(this.getAttribute('data-form-id'))
    this.uploadTokens = []
  }

  setupListeners () {
    super.setupListeners()

    this.handleForm = this.handleForm.bind(this)

    this.listeners.add(this.els.form.element, 'submit', this.handleForm)

    if(this.els.file?.exists ) {
      this.els.file.elements.forEach(fileEl => {
        this.listeners.add(
          fileEl,
          'change',
          this.handleFileChange.bind(this),
        )
      })
    }
  }

  async handleFileChange(e) {
    const files = Array.from(e.target.files)

    if (!files.length) return

    this.els.submit.element.classList.add('btn-Button-loading')

    try {
      const newUploadTokens = await Promise.all(files.map(async file => {
        if (file) {
          const fileUploadUrl = `https://tech21.zendesk.com/api/v2/uploads.json?filename=${
            encodeURIComponent(file.name)
          }`

          const response = await fetch(fileUploadUrl, {
            method: 'POST',
            headers: {
              'Content-Type': file.type,
            },
            body: file,
          })

          if (response.ok) {
            const responseData = await response.json()
            return responseData.upload.token
          } else {
            // Handle non-OK response
            console.error('Upload failed:', await response.text())
          }
        }
      }))

      this.uploadTokens = [...this.uploadTokens, ...newUploadTokens.filter(token => token !== undefined)]
    } catch (error) {
      // Handle network error
      console.error('Error during file upload:', error)
    } finally {
      this.els.submit.element.classList.remove('btn-Button-loading')
    }
  }


  async handleForm (e) {
    e.preventDefault()

    let customFields = []
    let thisValue

    for (const key in this.els) {
      if (this.els[key].exists && this.els[key].element?.dataset?.fieldId != null) {
        if (
          this.els[key].element.type === 'text' ||
          this.els[key].element.type === 'email'
        ) {
          thisValue = this.els[key].element.value
        }
        else if (
          this.els[key].element.tagName?.toLowerCase() === 'select'
        ) {
          thisValue = this.els[key].element.options[this.els[key].element.selectedIndex].value
        }
        else if (
          this.els[key].element.type === 'checkbox'
        ) {
          let checkedValues = []
          // if more than one, it's a selection of checkboxes
          if (this.els[key].elements.length > 1) {
            this.els[key].elements.forEach((element) => {
              if (element.type === 'checkbox' && element.checked) {
                checkedValues.push(element.value)
              }
            })
            thisValue = checkedValues
          }
          // if there's only one, it's a true/false
          else {
            thisValue = this.els[key].element.checked ? true : false
          }
        }

        customFields.push({
          id: this.els[key].element.dataset.fieldId,
          value: thisValue,
        })
      }
    }

    let fullName = ''
    if (this.els.firstName.element.value != null) {
      fullName = this.els.firstName.element.value
      if (this.els.lastName.element.value != null) {
        fullName = `${fullName} ${this.els.lastName.element.value}`
      }
    } else {
      fullName = this.els.email.element.value
    }

    const zendeskData = {
      request: {
        requester: {
          name: fullName,
          email: this.els.email.element.value,
        },
        subject: `${this.formTag} submission`,
        comment: {
          body: `${this.formTag} submission`,
        },
        ticket_form_id: this.formID,
        // tags: ['Warranty_Registration_Tech21'], // this is tags that will be added to the ticket
        custom_fields: customFields,
      },
    }

    if (this.uploadTokens.length > 0) {
      zendeskData.request.comment.uploads = this.uploadTokens
    }

    const url = 'https://tech21.zendesk.com/api/v2/requests.json'

    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(zendeskData),
    }).then(response => {
      if (response.ok) {
        this.els.form.element.setAttribute('aria-hidden', 'true')
        this.els.success.element.setAttribute('aria-hidden', 'false')
        this.els.error.element.setAttribute('aria-hidden', 'true')
      } else {
        this.els.error.element.setAttribute('aria-hidden', 'false')
      }
    })
  }
}

if (!customElements.get('zendesk-form')) {
  customElements.define('zendesk-form', ZendeskForm)
}
